import {_} from 'lodash';
import * as ko from "knockout";
import {ITableButton, ITableColumnDef, ITableDef, ITableNavigation, ITableSortDef, TKey} from "./index";
import {storeObservable} from "../../util/sessionstore";
import {getAnyValue, getSearchMarkup, isNullOrUndefined} from "../../util/misc";
import {KOSubscriptionTracker} from "../../util/KOSubscriptionTracker";
import {AllCheckbox} from "./AllCheckbox";
import basicHtml from './basic.html?raw';

export const template = basicHtml

export class TableBasic<T> {

    private kosub: KOSubscriptionTracker = new KOSubscriptionTracker()
    private readonly allCheckbox: AllCheckbox<T>;
    /** Filter + sort once per dependency change; page slice and totals read from this. */
    private readonly processedData: KnockoutComputed<T[]>;
    private readonly pageData: KnockoutComputed<T[]>;
    private readonly navigationParams: ITableNavigation | undefined = undefined;

    constructor(private def: ITableDef<T>) {
        if ('sort' in def) {
            const defaultSortDef: ITableSortDef<T> = {
                ascending: ko.observable(true),
                sortColumn: ko.observable(this.getColumnsArr()[0]?.key ?? ('' as keyof T)),
            };
            if (def.sort === true) {
                def.sort = defaultSortDef
            } else {
                _.defaults(def.sort, defaultSortDef)
            }
        }

        if ('navigation' in def) {
            const defaultNavigationDef = {
                pagenr: ko.observable(0),
                data: ko.observableArray(getAnyValue(this.def.data)),
                totalresults: ko.observable(0),
            };
            if (def.navigation === true) {
                def.navigation = defaultNavigationDef
            } else {
                _.defaults(def.navigation, defaultNavigationDef)
            }
        }

        this.processedData = ko.pureComputed(() => {
            let ret = this.getData()
            ret = this.getFilteredData(ret)
            ret = this.getSortedData(ret)
            return ret
        })

        this.pageData = ko.pureComputed(() => {
            const ret = this.processedData()
            const navigation = this.getNavigationDef();
            if (navigation) {
                const sortColumnDef = this.getSortColumnDef();
                if (sortColumnDef) {
                    const numSearchResults = storeObservable('num.search.results')();
                    const start = (navigation.pagenr?.() ?? 0) * numSearchResults;
                    const end = start + numSearchResults;
                    return ret.slice(start, end)
                }
            }
            return ret;
        })

        this.allCheckbox = new AllCheckbox<T>(() => this.pageData(), this.def.selected, this.def.rows?.checkbox?.show)

        const navigation = this.getNavigationDef()
        if (navigation) {
            const params: ITableNavigation = {
                data: this.processedData,
            }
            if (navigation.totalresults) {
                params.totalresults = navigation.totalresults
            }
            if (navigation.pagenr) {
                params.pagenr = navigation.pagenr
            }
            if (navigation.getNavComponent) {
                params.getNavComponent = navigation.getNavComponent
            }
            this.navigationParams = params

            this.kosub.subscribe(this.processedData, (rows: T[]) => {
                navigation.totalresults?.(rows.length)
            })
            navigation.totalresults?.(this.processedData().length)

            const searchkey = this.def.searchkey;
            if (searchkey) {
                this.kosub.subscribe(searchkey, () => navigation.pagenr?.(0))
                this.kosub.subscribe(storeObservable('num.search.results'), () => navigation.pagenr?.(0))
            }
            const sortDef = this.def.sort as ITableSortDef<T> | undefined
            if (sortDef?.ascending && sortDef?.sortColumn) {
                this.kosub.subscribe([sortDef.ascending as KnockoutSubscribable<any>, sortDef.sortColumn as KnockoutSubscribable<any>], () => navigation.pagenr?.(0))
            }
        }
    }

    private getColumnsArr(): ITableColumnDef<T>[] {
        return getAnyValue(this.def.columns)
    }

    private getColumnDef(key: TKey<T>): ITableColumnDef<T> | undefined {
        return this.getColumnsArr().find(c => c.key == key)
    }


    private getData(): T[] {
        return getAnyValue(this.def.data)
    }

    private getFilteredData(data: T[]): T[] {
        if (this.def.searchkey?.() === null) {
            return []
        }
        if (!this.def.searchkey || !this.def.searchkey?.()) {
            return data
        }
        const searchableColumns = this.getColumnsArr()
            .filter(o => getAnyValue(o.searchable))
        const searchkey = this.def.searchkey()?.toLowerCase() ?? '';
        if (!searchkey) {
            return data
        }
        return data.filter(o => !!searchableColumns.find(col => {
            // Match raw cell text only — do not run getSearchMarkup during filter (expensive on large sets).
            const content = col.getContent?.(o, col, o[col.key]) ?? o[col.key]
            if (isNullOrUndefined(content)) {
                return false
            }
            return String(content).toLowerCase().includes(searchkey)
        }))
    }

    private getContent(obj: T, col: ITableColumnDef<T>) {
        const content = col.getContent?.(obj, col, obj[col.key]) ?? obj[col.key]
        if (isNullOrUndefined(content)) {
            return null
        }
        let ret: string = content
        const searchkey = this.def.searchkey?.();
        if (searchkey) {
            if (col.getSearchMarkup) {
                ret = col.getSearchMarkup(obj, col, content)
            } else {
                ret = getSearchMarkup(content, searchkey)
            }
        }
        if ((col.contentType ?? 'text') == 'text') {
            ret = String(ret)
                .replace(/<(?![/]?mark>)/gmi, '&lt;')
                .replace(/(?<!<[/]?mark)>/gmi, '&gt;')
            ;
        }
        return ret
    }

    private getSortColumnDef(): ITableColumnDef<T> | undefined {
        const key = this.getSortDef()?.sortColumn?.() ?? this.getColumnsArr()[0]?.key;
        return key ? this.getColumnDef(key) : undefined;
    }

    private getSortDef(): ITableSortDef<T> | undefined {
        return this.def.sort === true ? undefined : this.def.sort as ITableSortDef<T> | undefined;
    }

    private getSortedData(data: T[]): T[] {
        const sortColumnDef = this.getSortColumnDef();
        if (!sortColumnDef) {
            return data;
        }
        let sortFn: (obj: T) => (string | number | boolean)
        if (typeof sortColumnDef.getSortValue === 'function') {
            sortFn = (o: T) => (sortColumnDef.getSortValue as Function)(o)
        } else {
            sortFn = (o: T) => o[sortColumnDef.key as TKey<T>] as (string | number | boolean)
        }
        const sortArr = [sortFn]
        if (sortColumnDef.key != 'id' && this.getColumnDef('id' as TKey<T>)) {
            sortArr.push(o => o['id' as TKey<T>] as number)
        }
        const ret = _.sortBy(data, sortArr)
        const ascending = this.getSortDef()?.ascending?.();
        return ascending !== false
            ? ret
            : ret.reverse()
    }

    private getNavigationDef(): ITableNavigation | undefined {
        return this.def.navigation === true ? undefined : this.def.navigation as ITableNavigation | undefined;
    }

    private getHeaderContent(col: ITableColumnDef<T>): any {
        return col.header?.getContent?.(col)
            ?? this.def.header?.getContent?.(col)
            ?? (col.label ?? col.key)
    }

    private getHeaderAttr(col: ITableColumnDef<T>): any {
        return {
            title: col.header?.getTitle?.(col) ??
                this.def.header?.getTitle?.(col) ??
                null,
        }
    }

    private getRowAttr(obj: T): any {
        const defaults = this.def.rows?.getAttr?.(obj) ?? {};
        return _.defaults(defaults, {
            tabIndex: this.def.rows?.onClick ? 0 : null,
            title: this.def.rows?.getTitle?.(obj) ?? null,
        })
    }

    private getCellAttr(obj: T, col: ITableColumnDef<T>): any {
        return {
            title: col.getTitle?.(obj) ?? null
        }
    }

    private onCheckboxToggle(obj: T) {
        this.def.rows?.checkbox?.onToggle?.(obj)
        return true
    }

    private getButtons(col: ITableColumnDef<T>): ITableButton<T>[] | null {
        if (!col.buttons) {
            return null
        }
        return getAnyValue(col.buttons)
    }

    private getNavigationParams(): ITableNavigation | undefined {
        return this.navigationParams
    }

    private validateCheckbox(): boolean {
        if (!this.def.rows?.checkbox) {
            return false
        }
        return this.def.rows?.checkbox?.validate?.() ?? true
    }

    private showCheckbox(obj: T): boolean {
        return this.def.rows?.checkbox?.show?.(obj) ?? true
    }
    
    private onKeyDown(obj: T, event: JQuery.KeyboardEventBase): boolean | void {
        if (event.key == 'Enter') {
            return this.def.rows?.onClick?.(obj)
        }
        if (event.key == 'Space') {
            return this.onCheckboxToggle(obj)
        }
        return true
    }

    dispose() {
        this.kosub.unsuscribeAll()
        this.processedData.dispose()
        this.pageData.dispose()
    }

}
